// @spec GHG-STR-012
'use client';

import { FileUp, Loader2, Upload, X } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';

import { useGhgOcrUpload } from '../../api/use-ghg-ocr-upload';
import type { GhgDocumentType } from '../../types/ocr';
import {
  GHG_DOCUMENT_TYPE_DESCRIPTIONS,
  GHG_DOCUMENT_TYPE_OPTIONS,
  MAX_OCR_FILE_SIZE,
  validateOcrFile,
} from '../../types/ocr';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Select } from '@/components/ui/select';
import { useTranslation } from 'react-i18next';

interface GhgDocumentUploadProps {
  facilityId?: string;
  reportingPeriodId?: string;
  defaultDocumentType?: GhgDocumentType;
  onUploadComplete: (uploadId: string) => void;
  onError: (error: Error) => void;
}

/**
 * Drag-and-drop upload component for GHG documents.
 * Supports PDF/image and XLSX files up to 20MB.
 */
export function GhgDocumentUpload({
  facilityId,
  reportingPeriodId,
  defaultDocumentType,
  onUploadComplete,
  onError,
}: GhgDocumentUploadProps) {
  const { t } = useTranslation('ghg');
  const [documentType, setDocumentType] = useState<GhgDocumentType>(
    defaultDocumentType ?? 'electricity_bill'
  );
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  const { isUploading, progress, upload, reset } = useGhgOcrUpload();

  const onDrop = useCallback((acceptedFiles: File[]) => {
    setValidationErrors([]);

    const file = acceptedFiles[0];
    if (!file) {
      return;
    }

    const errors = validateOcrFile(file);

    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }

    setSelectedFile(file);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/png': ['.png'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    },
    maxSize: MAX_OCR_FILE_SIZE,
    maxFiles: 1,
    noClick: isUploading,
    noKeyboard: isUploading,
    disabled: isUploading,
  });

  const handleUpload = async () => {
    if (!selectedFile) return;

    const result = await upload(selectedFile, documentType, facilityId, reportingPeriodId);

    if (result) {
      onUploadComplete(result.id);
      setSelectedFile(null);
      reset();
    } else {
      onError(new Error('Upload failed'));
    }
  };

  const handleClearFile = () => {
    setSelectedFile(null);
    setValidationErrors([]);
    reset();
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${String(bytes)} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileUp className="h-5 w-5" />
          Upload Document for OCR
        </CardTitle>
        <CardDescription>{t('ghgDocumentUpload.cardDesc_upload_utility_bills_or_fuel_receipts_fo')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Document Type Selector */}
        <div className="space-y-2">
          <label className="text-sm font-medium">Document Type</label>
          <Select
            options={GHG_DOCUMENT_TYPE_OPTIONS}
            value={documentType}
            onChange={(value) => {
              setDocumentType(value as GhgDocumentType);
            }}
            placeholder={t('placeholder.selectDocumentType')}
            disabled={isUploading}
          />
          <p className="text-sm text-muted-foreground">
            {GHG_DOCUMENT_TYPE_DESCRIPTIONS[documentType]}
          </p>
        </div>

        {/* Dropzone */}
        <div
          {...getRootProps()}
          className={`
            relative rounded-lg border-2 border-dashed p-8 text-center transition-colors
            ${isDragActive ? 'border-primary bg-primary/5' : 'border-muted-foreground/25'}
            ${isUploading ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:border-primary/50'}
          `}
        >
          <input {...getInputProps()} aria-label={t('ghgDocumentUpload.ariaLabel_file_upload')} />

          {selectedFile ? (
            <div className="space-y-2">
              <div className="flex items-center justify-center gap-2">
                <FileUp className="h-8 w-8 text-primary" />
                <div className="text-left">
                  <p className="font-medium">{selectedFile.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {formatFileSize(selectedFile.size)}
                  </p>
                </div>
                {!isUploading && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleClearFile();
                    }}
                    aria-label={t('ghgDocumentUpload.ariaLabel_remove_file')}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <Upload className="mx-auto h-12 w-12 text-muted-foreground" />
              <div>
                <p className="font-medium">
                  {isDragActive ? 'Drop the file here' : 'Drag & drop a file here'}
                </p>
                <p className="text-sm text-muted-foreground">or click to select a file</p>
              </div>
              <p className="text-xs text-muted-foreground">
                PDF, PNG, JPG, JPEG, or XLSX (max 20MB)
              </p>
            </div>
          )}
        </div>

        {/* Validation Errors */}
        {validationErrors.length > 0 && (
          <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
            {validationErrors.map((error, index) => (
              <p key={index}>{error}</p>
            ))}
          </div>
        )}

        {/* Upload Progress */}
        {isUploading && (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span>Uploading...</span>
              <span>{progress}%</span>
            </div>
            <Progress value={progress} aria-label={t('ghgDocumentUpload.ariaLabel_upload_progress')} />
          </div>
        )}

        {/* Upload Button */}
        <Button
          onClick={() => void handleUpload()}
          disabled={!selectedFile || isUploading}
          className="w-full"
        >
          {isUploading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Uploading...
            </>
          ) : (
            <>
              <Upload className="mr-2 h-4 w-4" />
              Upload for OCR Processing
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
}
